import { z } from 'zod';
import { baseQueryArgs, baseExtraOptions, rsosApi } from '../rsosApi';
import { setCreateConversationLoading } from './conversationsSlice';

const DirectoryEntrySchema = z.object({
  identifier: z.string(),
  name: z.string(),
  type: z.enum(['conference', 'transfer']),
});
const DirectoryResponseSchema = z.array(DirectoryEntrySchema);

const getTCAPIBaseEndpoint = (orgName: string) => {
  return `v1/text_to_911/tcapi/psap/${orgName}`;
};

export const THIRTY_DAYS = 1000 * 60 * 60 * 24 * 30;
export const TWO_HOURS = 1000 * 60 * 60 * 2;

/**
 * Extends the rsosApi for TCAPI endpoints.
 * Authorization header must be manually included to tell RTK which token to use
 * otherwise it will use the access token
 */
export const tcapiAPI = rsosApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * Fetches a phone number's ongoing conversation metadata.
     * @param callerID - phone number
     * @param orgName - Name of authority the user belongs to
     */
    getTCAPIConversationMetadata: builder.query({
      query: ({ callerID, orgName }) => ({
        ...baseQueryArgs,
        url: `${getTCAPIBaseEndpoint(orgName)}/session/+${callerID}`,
        method: 'GET',
      }),
      providesTags: ['TCAPIMetaData'],
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    /**
     * Ends a conversation with the given phone number
     * @param callerID - phone number
     * @param orgName - Name of authority the user belongs to
     */
    endTCAPIConversation: builder.mutation({
      query: ({ callerID, orgName }) => ({
        ...baseQueryArgs,
        url: `${getTCAPIBaseEndpoint(orgName)}/session/+${callerID}`,
        method: 'PUT',
        body: { status: 'inactive' },
      }),
      invalidatesTags: ['TCAPIMetaData'],
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    /**
     * Starts a conversation with the given phone number
     */
    createTCAPIConversation: builder.mutation({
      query: ({ callerID, orgName }) => ({
        ...baseQueryArgs,
        url: `${getTCAPIBaseEndpoint(orgName)}/session/+${callerID}`,
        method: 'PUT',
        body: { acceptStatus: 'accepted' },
      }),
      onQueryStarted: async ({ callerID }, { dispatch, queryFulfilled }) => {
        try {
          dispatch(setCreateConversationLoading({ callerID, isLoading: true }));
          await queryFulfilled;
        } finally {
          dispatch(
            setCreateConversationLoading({ callerID, isLoading: false }),
          );
        }
      },
      invalidatesTags: ['TCAPIMetaData'],
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    /**
     * Fetches the agency directory for transfer/conference.
     * @param orgName - Name of authority the user belongs to
     */
    getTCAPIAgencyDirectory: builder.query({
      query: ({ orgName }) => ({
        ...baseQueryArgs,
        url: `v1/text_to_911/tcapi/agencies/${orgName}/directory`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => {
        const entries = DirectoryResponseSchema.parse(response);
        return entries.filter(entry => entry.type === 'transfer');
      },
      providesTags: ['TCAPIAgencyDirectory'],
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
  }),
  overrideExisting: 'throw',
});

export const {
  useGetTCAPIConversationMetadataQuery,
  useLazyGetTCAPIConversationMetadataQuery,
  useEndTCAPIConversationMutation,
  useCreateTCAPIConversationMutation,
  useGetTCAPIAgencyDirectoryQuery,
} = tcapiAPI;
